import { useCallback, useContext, useEffect, useState } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react";
import { UI } from "@wwimmo/ui";
import { useParams } from "react-router";
import * as Screens from "src/screens";
import { getDateWithLongMonth } from "src/utils/Date";
import { Route, selectRoute } from "src/config/routes";
import { useTranslation } from "react-i18next";
import styles from "./LiveMode.module.css";
import { useHistory } from "react-router-dom";
import { CloseLiveMode } from "./close/CloseLiveMode";
import { ProcessPanelLiveMode } from "./processpanel/ProcessPanelLiveMode";
import { InternalCommentLiveMode } from "./internalcomment/InternalCommentLiveMode";
import { DocumentsLiveMode } from "./documents/DocumentsLiveMode";
import { Attendance } from "src/screens/assemblies/assembly/attendance/Attendance";
import { MessageType } from "src/components/notifications/Notifier";
import { ProtocolText } from "src/screens/assemblies/assembly/protocol/ProtocolText";
import { AssemblyVoting } from "./voting/AssemblyVoting";
import { AssemblyVotingList } from "./voting/list/AssemblyVotingList";
import { getHtmlStringForPreview } from "src/utils/HTMLUtils";

interface Params {
    assemblyid?: string;
}

const LiveModeBase = observer(() => {
    const { agendaitemStore, assemblyStore, navStore, authStore, uiStore, votingStore } = useContext(RootStoreContext);
    const { assemblyid } = useParams<Params>();
    const { t } = useTranslation();
    const history = useHistory();

    const [windowWidth, setWindowWidth] = useState<number>(window.innerWidth);

    // Update width on resize
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleNavigateBack = useCallback(() => {
        if (agendaitemStore.currentAgendaItems.length > 0) {
            agendaitemStore.navigateBack();
        }
    }, [agendaitemStore]);

    const handleNavigateForward = useCallback(() => {
        if (agendaitemStore.currentAgendaItems.length > 0) {
            agendaitemStore.navigateForward();
        }
    }, [agendaitemStore]);

    useEffect(() => {
        if (assemblyid && assemblyStore.currentAssembly === undefined) {
            assemblyStore.loadCurrentAssembly(assemblyid);
        }
        if (!assemblyStore.assembyLiveModeHasStopped) {
            assemblyStore.startSubscriptionHasAssemblyLiveModeChanged(assemblyid || "");
        }

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (assemblyStore.currentAssembly) {
            navStore.setTitle(
                `${assemblyStore.currentAssembly?.title} am ${getDateWithLongMonth(
                    assemblyStore.currentAssembly?.date || ""
                )}` || ""
            );
            agendaitemStore.setCurrentAssemblyId(assemblyStore.currentAssembly.id);

            agendaitemStore.loadAgendaItems();
            assemblyStore.setIsAttendanceLiveModeTabActive(true);
        }

        navStore.setActionButtonMiddle(null);
        navStore.setActionButtonRight(null);
        navStore.setBackbutton(
            selectRoute(Route.assemblies, authStore.user?.role),
            t("screens.assemblies.sidebar_title").toString()
        );
    }, [assemblyStore, authStore, navStore, t, assemblyStore.currentAssembly, assemblyid, agendaitemStore]);

    useEffect(() => {
        if (assemblyStore.assembyLiveModeHasStopped) {
            assemblyStore.setAssemblyState(5);
            uiStore.printStatusMessage(t("screens.assemblies.livemode.successfull_closed"), MessageType.INFO);
            history.push(
                selectRoute(Route.assemblyDetails, authStore.user?.role, {
                    assemblyid: assemblyStore.currentAssembly?.id || ""
                })
            );
            assemblyStore.stopSubscriptionHasAssemblyLiveModeChanged();
            assemblyStore.setAssembyLiveModeHasStopped(false);
        }
    }, [
        assemblyStore.assembyLiveModeHasStopped,
        authStore.user?.role,
        history,
        uiStore,
        t,
        assemblyStore.currentAssembly?.id,
        assemblyStore
    ]);

    const onClickStopLiveMode = useCallback(async () => {
        await assemblyStore.updateAssemblyLiveMode(
            false,
            t("screens.assemblies.livemode.successfull_closed").toString()
        );
        history.push(
            selectRoute(Route.assemblyDetails, authStore.user?.role, {
                assemblyid: assemblyStore.currentAssembly?.id || ""
            })
        );
        assemblyStore.setIsAttendanceLiveModeTabActive(false);
        assemblyStore.stopSubscriptionHasAssemblyLiveModeChanged();
    }, [assemblyStore, authStore.user?.role, history, t]);

    if (!assemblyStore.currentAssembly) {
        return <Screens.Loading />;
    }

    // If screen width is less than 768px, show a warning
    if (windowWidth < 768) {
        return (
            <div style={{ padding: "20px", textAlign: "center", fontWeight: "bold" }}>
                Your screen is too small. Please use a bigger screen.
            </div>
        );
    }

    const currentItem = agendaitemStore.currentAgendaItem;

    // Find the parent if current item is a child
    const parentItem = agendaitemStore.currentAgendaItems.find((parent) =>
        parent.agendaitemchild?.some((child) => child.id === currentItem?.id)
    );

    // Construct display text
    const agendaText =
        parentItem && currentItem
            ? `${parentItem.sort}.${currentItem.sort} ${currentItem.text}`
            : `${currentItem?.sort}. ${currentItem?.text}`;

    const renderNavigation = () => (
        <div className={styles.TitleRow}>
            <button className={styles.NavButton} onClick={handleNavigateBack}>
                <UI.Icon icon={UI.SVGIcon.ChevronLeft} size={22} />
                <div>{t("screens.assemblies.livemode.forward").toString()}</div>
            </button>
            <div className={styles.Title}>
                {assemblyStore.isCloseLiveModeTabActive
                    ? t("screens.assemblies.livemode.close")
                    : assemblyStore.isAttendanceLiveModeTabActive
                      ? t("screens.assemblies.attendanceList")
                      : agendaText}
            </div>
            <button className={styles.NavButton} onClick={handleNavigateForward}>
                <div>{t("screens.assemblies.livemode.goback").toString()}</div>
                <UI.Icon icon={UI.SVGIcon.ChevronRight} size={22} />
            </button>
        </div>
    );

    return (
        <>
            {assemblyStore.currentAssembly.livemode ? (
                <div className={styles.Container}>
                    <ProcessPanelLiveMode />
                    <div className={styles.MainRow}>
                        {renderNavigation()}

                        {assemblyStore.isCloseLiveModeTabActive ? (
                            <CloseLiveMode />
                        ) : assemblyStore.isAttendanceLiveModeTabActive ? (
                            <Attendance isLiveModeSmallView={windowWidth <= 1024} />
                        ) : (
                            <>
                                <UI.Container fluid className={`${styles.AgendaItemCard} mb-4 p-3`}>
                                    <div className={styles.agendaItemText}>{agendaText}</div>
                                    <div
                                        className={styles.agendaItemDescription}
                                        dangerouslySetInnerHTML={{
                                            __html: getHtmlStringForPreview(
                                                agendaitemStore.currentAgendaItem?.description
                                            )
                                        }}
                                    />
                                </UI.Container>
                                {navStore.hasFeatureFlagAssemblyVoting ? (
                                    <>
                                        <AssemblyVotingList agendaitemId={agendaitemStore.currentAgendaItem?.id} />
                                        {votingStore.votingList.length === 0 ||
                                        !votingStore.selectedVoting.votingid ? undefined : (
                                            <AssemblyVoting assemblyid={assemblyid} />
                                        )}
                                    </>
                                ) : undefined}

                                <UI.Container fluid className={`${styles.ProtocolTextCard} mb-4 p-3`}>
                                    <label className={styles.ProtocolLabel}>
                                        {t("screens.assemblies.livemode.protocol_comment").toString()}
                                    </label>
                                    <ProtocolText key={agendaitemStore.currentAgendaItem?.id} />
                                </UI.Container>
                            </>
                        )}
                        <div className={styles.SideCardsRow}>
                            {agendaitemStore.currentAgendaItem ? <InternalCommentLiveMode /> : undefined}
                            {agendaitemStore.currentAgendaItem ? <DocumentsLiveMode /> : undefined}
                        </div>
                        {renderNavigation()}
                    </div>
                </div>
            ) : undefined}
        </>
    );
});

export const LiveMode = Sentry.withProfiler(LiveModeBase);
